import { PiniaPluginContext } from 'pinia';
import { InitializableStore } from '../types/storeTypes';

export function storeInitializationPlugin({ store }: PiniaPluginContext) {
  // Add initialization tracking to all stores
  if (!('isInitialized' in store.$state)) {
    store.$state.isInitialized = false;
  }
  
  // Add isInitializing flag to prevent concurrent initializations
  if (!('isInitializing' in store.$state)) {
    store.$state.isInitializing = false;
  }
  
  // Only add initialization logic to stores that have an initialize method
  if ('initialize' in store) {
    const typedStore = store as unknown as InitializableStore;
    
    // Save the original initialize method
    const originalInitialize = typedStore.initialize;
    
    // Replace with enhanced version
    typedStore.initialize = async function() {
      // Skip if already initialized or initializing
      if (store.$state.isInitialized || store.$state.isInitializing) {
        console.log(`Store ${store.$id} already initialized or initializing, skipping.`);
        return;
      }
      
      console.log(`Initializing store: ${store.$id}`);
      store.$state.isInitializing = true;
      
      try {
        // Call the original initialize method
        if (originalInitialize) {
          const result = await originalInitialize.call(this);
          // Handle both boolean and void return types
          if (result === false) {
            // Only set to false if explicitly returned false
            store.$state.isInitialized = false;
            console.error(`Store ${store.$id} initialization failed.`);
          } else {
            // For void returns or true, consider it successful
            store.$state.isInitialized = true;
            console.log(`Store ${store.$id} initialized successfully.`);
          }
        } else {
          // No initialize method, just mark as initialized
          store.$state.isInitialized = true;
        }
      } catch (error) {
        console.error(`Failed to initialize store ${store.$id}:`, error);
        // Reset initialization state on error
        store.$state.isInitialized = false;
      } finally {
        store.$state.isInitializing = false;
      }
    };
    
    // Auto-initialize stores after app is mounted
    if (typeof window !== 'undefined') {
      // Use setTimeout to defer execution to the next event loop
      setTimeout(() => {
        // Only auto-initialize specific stores if needed
        const storesForAutoInit = ['LiveUserActivityStore', 'offerStore', 'userStore', 'authStore', 'appConfigStore', 'NotificationsStore', 'PayoutStore', 'leaderboardStore', 'OfferStatusStore', 'SpinTheWheelStore', 'inHouseTasks'];
        if (storesForAutoInit.includes(store.$id)) {
          typedStore.initialize?.();
        }
      }, 0);
    }
  }
}
